import React, { useCallback } from "react";
import { StoreAction } from "./actions";
import { AppCtx, StoreWithDispatchAction } from "../hooks/use-store";
import { LoadingState } from "../../domain/data-layer/loading-state";
import { Maybe } from "../../utils/fp/small-fp-utils";
import { StoreState } from "./state";
import { PublicConfigurationT, SwarovskiOptikSegmentKey } from "@swo/shared";
import { UserAuthToken } from "../../domain/auth/user-auth/auth-client";
import { SalesforceUserProfileT } from "../../domain/io/salesforce/fetch-user-profile";
import { UserAspect, LoggedInUserData } from "../aspects/user-aspect/aspect-types";

export type AppStoreState = {
  userPreferredSegmentKey: Maybe<SwarovskiOptikSegmentKey>;
  userPreferredLocale: Maybe<string>;
  showSegmentSelector: boolean;
  withLocaleMismatchOverlay: boolean;
  authServiceHost: URL;
  config: PublicConfigurationT;
  locationOrigin: URL;
  headerFloatingDisabled: boolean;
  auth: AppStoreUserAspectState;
};

export type AppStoreUserAspectLoggedInState = {
  user: "logged-in-user";
  authToken: UserAuthToken;
  userData: LoggedInUserData | undefined;
  userProfileSf: LoadingState<SalesforceUserProfileT>;
};

export type AppStoreUserAspectAnonymousState = {
  user: "anonymous-user";
  authToken: UserAuthToken;
  userData: undefined;
};

export type AppStoreUserAspectState =
  | AppStoreUserAspectLoggedInState
  | AppStoreUserAspectAnonymousState
  | undefined;

export const AppStoreCtxProvider: React.FC<
  React.PropsWithChildren<{
    state: StoreState;
    userAspect: UserAspect | undefined;
    dispatch: React.Dispatch<StoreAction>;
  }>
> = props => {
  const { state, dispatch, userAspect } = props;

  const publicDispatch = useCallback(
    (action: StoreWithDispatchAction) => {
      if (
        action.type === "user-logged-in" ||
        action.type === "user-logged-out" ||
        action.type === "token-appears-to-be-invalid"
      ) {
        return userAspect?.dispatch(action);
      } else {
        return dispatch(action);
      }
    },
    [dispatch, userAspect?.dispatch, state],
  );

  function mapState() {
    const ensuredState: AppStoreState = {
      auth: props.userAspect,
      userPreferredLocale: state.userPreferredLocale,
      userPreferredSegmentKey: state.userPreferredSegmentKey,
      showSegmentSelector: state.showSegmentSelector,
      withLocaleMismatchOverlay: state.withLocaleMismatchOverlay,
      authServiceHost: props.state.authServiceHost,
      config: props.state.config,
      locationOrigin: props.state.locationOrigin,
      headerFloatingDisabled: props.state.headerFloatingDisabled,
    };

    return { state: ensuredState, dispatch: publicDispatch };
  }

  const value = mapState();

  return <AppCtx.Provider value={value}>{props.children}</AppCtx.Provider>;
};
